/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavCard from "examples/Sidenav/SidenavCard";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import Utils from "Utils";

// Soft UI Dashboard PRO React context
import { useSoftUIController, setMiniSidenav, setConfirmations } from "context";

import { getData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

let refreshTimeout = null;
let lastConfirmations = null;
let confirmationsRequestInFlight = false;

function Sidenav({ color, brandShort, brandFull, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(null);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(null);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav, sports_numbers, platform, permissions, confirmations } = controller;
  const location = useLocation();
  const { pathname, hash } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const itemName = pathname.split("/").slice(1)[1];
  const leafName = pathname.split("/").slice(1)[2];
  const [fileUrl, setFileUrl] = useState();

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    lastConfirmations = confirmations;
  }, [confirmations]);

  
  function checkOpened(name, route) {
    let opened = false;
        let nameToCheck = name;
    if (collapseName == "monitoring") {
      nameToCheck = name.split("(")[0].trim();
    }
    if (openNestedCollapse && openNestedCollapse.indexOf(nameToCheck) > -1) {
      opened = true;
    }
    if (openNestedCollapse == null) {
      if (itemName.toLowerCase() == nameToCheck.toLowerCase()) opened = true;
    }
    // if (
    //   (openNestedCollapse && openNestedCollapse.indexOf(nameToCheck) > -1) ||
    //   route.indexOf(leafName) > -1
    // ) {
    //   opened = true;
    // }
    return opened;
  }

  function clickNestedCollapsed(e, name) {
    e.preventDefault();
    let openNested = false;
    if (openNestedCollapse === name) {
      //console.log(name, " it was open, must be closed");
    } else {
      openNested = name;
      //console.log("Must open");
    }
    setOpenNestedCollapse(openNested);
  }

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    if (collapseName !== openCollapse) setOpenCollapse(collapseName);

    if (itemName && itemName !== openNestedCollapse) {
      if (openNestedCollapse) {
        if (
          collapseName == "monitoring" &&
          openNestedCollapse.toLowerCase().indexOf(itemName) < 0
        ) {
          setOpenNestedCollapse(itemName.charAt(0).toUpperCase() + itemName.slice(1));
        } else {
          if (itemName) setOpenNestedCollapse(itemName.charAt(0).toUpperCase() + itemName.slice(1));
        }
      } else {
        if (itemName) setOpenNestedCollapse(itemName.charAt(0).toUpperCase() + itemName.slice(1));
      }
    }

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleCollapse = (e, key) => {
    e.preventDefault();
    openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key);
  };

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href, visible, dynamic, icon }) =>
      visible ? (
        href ? (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} nested />
          </Link>
        ) : (
          <NavLink
            to={hash !== "" ? route + "" + hash : route}
            key={key}
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} active={route === pathname} nested icon={icon} />
          </NavLink>
        )
      ) : null
    );

    return template;
  };

  // Render the all the collapses from the routes.js
  const renderCollapse = (collapses) => {
    return collapses.map(({ name, collapse, route, href, key, visible, dynamic, icon }) => {
      let returnValue;
      if (!visible) return null;

      if (dynamic) {
        if (
          (pathname.indexOf(route) > -1 || pathname.indexOf("monitoring") > -1) &&
          sports_numbers
        ) {
          let tempRoute = Utils.collectDynamicRoutes(route, null, null, sports_numbers);
          icon = tempRoute.icon;
          if (name.indexOf("(") > -1) name = name.split("(")[0].trim();
          name = name + " " + tempRoute.name;
          collapse = tempRoute.collapse;
          if (
            !openNestedCollapse &&
            collapseName == "monitoring" &&
            (itemName == "inplay" || itemName == "pregame")
          ) {
            //console.log("setOpenNestedCollapse from renderCollapse");
            if (name.toLowerCase().indexOf(itemName) > -1 && openNestedCollapse == null)
              setOpenNestedCollapse(name);
          }
        }
      }

      if (collapse) {
        returnValue = (
          <SidenavItem
            // title={
            //   key +
            //   "-" +
            //   name +
            //   " - " +
            //   leafName +
            //   " -- " +
            //   itemName +
            //   " - " +
            //   collapseName +
            //   " - " +
            //   openNestedCollapse
            // }
            key={key}
            name={name}
            icon={icon}
            active={key === itemName}
            open={
              openNestedCollapse != null
                ? openNestedCollapse
                  ? key === openNestedCollapse.toLowerCase()
                    ? true
                    : openNestedCollapse.indexOf("(") > -1
                    ? checkOpened(name, route)
                    : false
                  : checkOpened(name, route)
                : itemName == name.toLowerCase()
                ? true
                : false
            }
            onClick={(e) => clickNestedCollapsed(e, name)}
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem name={name} active={key === itemName} icon={icon} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });
  };

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route, visible, dynamic }) => {
      let returnValue;
      if (!visible) return null;
      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={key === openCollapse}
              onClick={(e) => handleCollapse(e, key)}
              className="to-collapse"
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }

      return returnValue;
    }
  );

  async function changeLogo(id) {
    let getPlatformEndpoint = `/api/v1/platforms/${id}`;
    let response = await getData(getPlatformEndpoint, { Authorization: getAuthorizationHeader() });
    let logoname = null;
    if (response) {
      logoname = response && response.data.length ? response.data[0].logo : null;
    }

    if (logoname) {
      setFileUrl(process.env.REACT_APP_AUTH_URL + `/api/v1/files/preview/${logoname}`);
    } else {
      setFileUrl(null);
    }
  }

  async function initData() {
    if (confirmationsRequestInFlight) return;
    confirmationsRequestInFlight = true;

    try {
      let getConfirmationsCountEndpoint = `/api/v1/bets/settlements/unconfirmed/count`;
      let confirmationsResponse = await getData(getConfirmationsCountEndpoint, {
        Authorization: getAuthorizationHeader(),
      });

      const nextConfirmations = confirmationsResponse != null ? confirmationsResponse.data : "";
      if (lastConfirmations !== nextConfirmations) {
        lastConfirmations = nextConfirmations;
        setConfirmations(dispatch, nextConfirmations);
      }
    } finally {
      confirmationsRequestInFlight = false;
    }
  }

  function startTimerCall() {
    if (refreshTimeout) return;
    refreshTimeout = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      initData();
    }, 60 * 1000);
  }

  useEffect(() => {
    if (platform.value) {
      changeLogo(platform.value);
    }
  }, [platform.value]);

  useEffect(() => {
    if (permissions && permissions.length > 0) {
      if (routes.find((item) => item.key === "settlement")) {
        initData();
        startTimerCall();
      } else {
        // console.log("Permissions Changed and no Settlement");
        clearInterval(refreshTimeout);
        refreshTimeout = null;
      }
    }
  }, [permissions]);

  useEffect(() => {
    return () => {
      if (refreshTimeout) {
        clearInterval(refreshTimeout);
        refreshTimeout = null;
      }
    };
  }, []);

  return pathname.indexOf("login") > -1 ? (
    <></>
  ) : (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          sx={{ display: fileUrl ? "none" : "" }}
        >
          {brandShort && (
            <SoftBox
              component="img"
              src={miniSidenav ? brandShort : brandFull}
              alt={brandName}
              width={miniSidenav ? "2rem" : "8rem"}
              sx={{ transform: "translate(25%)" }}
            />
          )}
        </SoftBox>
        <img
          src={fileUrl ? fileUrl : ""}
          style={{
            display: fileUrl ? "" : "none",
            margin: "auto",
            maxWidth: "200px",
            maxHeight: "90px",
          }}
        />
        <SoftBox
          // width={!brandName && "100%"}
          width="80% !important"
          textAlign="right !important"
          height="20px"
          lineHeight="20px !important"
          sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
        >
          <SoftTypography
            variant="button"
            fontWeight="medium"
            sx={{ fontSize: ".75rem", textAlign: "right !important", width: "inherit !important" }}
          >
            {brandName}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      <Divider />
      <List className="root-list">{renderRoutes}</List>
      <SoftBox pt={2} my={2} mx={2}>
        <SidenavCard />
      </SoftBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brandShort: "",
  brandFull: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brandShort: PropTypes.string,
  brandFull: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
